"use client";

import { lazy } from "react";
import type { ToolUIMeta, ToolUIRegistry } from "../../tool-renderer";

/**
 * Views for the `meeting-logistics` capability. The view files themselves stay
 * in `../ms-graph/` because they share the calendar grid (`calendar-views`) and
 * attendee formatting with `getCalendarEvents`, which remained on that
 * capability — only registry ownership moves here, mirroring the server split.
 *
 * `findRooms` and `createEvent` have no custom view: rooms narrate as text and
 * booking renders through the HITL approval card.
 */
export const meetingLogisticsToolRegistry = {
  findMeetingTimes: {
    kind: "standalone",
    category: "research",
    view: lazy(() =>
      import("../ms-graph/find-meeting-times").then(
        ({ FindMeetingTimesToolView }) => ({
          default: FindMeetingTimesToolView,
        })
      )
    ),
  },
  getSchedule: {
    kind: "standalone",
    category: "research",
    view: lazy(() =>
      import("../ms-graph/get-schedule").then(({ GetScheduleToolView }) => ({
        default: GetScheduleToolView,
      }))
    ),
  },
} satisfies ToolUIRegistry;

/**
 * Namespaced variant keyed by the progressive-disclosure inner-tool name
 * `meeting-logistics_${toolName}`. Without this, a PD tool part resolves to no
 * view and falls back to `GenericToolView` — which for `findMeetingTimes` means
 * losing the slot-picker card and, with it, the booking dialog mounted inside
 * it. The bare-name entries above stay in the static tier (still required by
 * `AllToolNames` and used by legacy persisted chats). See ADR-057 and ADR-085.
 */
export const meetingLogisticsProgressiveDisclosureToolRegistry: Record<
  string,
  ToolUIMeta
> = Object.fromEntries(
  Object.entries(meetingLogisticsToolRegistry).map(([name, meta]) => [
    `meeting-logistics_${name}`,
    meta,
  ])
);
